import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import ThreeDModel, { GENERATED_TILT_RADIANS } from '../model/3DModel';
import type { ObjectModel } from '../../lib/types';

const ZOOM_HINT =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    ? 'Pinch or ⌘ + scroll to zoom'
    : 'Pinch or Ctrl + scroll to zoom';

/** One body, one viewpoint. The concept pairs a front with a side throughout. */
export const TwinView: React.FC<{
  model: ObjectModel | null;
  side?: boolean;
  projected?: boolean;
  /** True for meshes from /generate, which carry the average pose's lean. */
  generated?: boolean;
  /** Key of a measurement to draw on the mesh, e.g. 'waist'. */
  showMeasurement?: string;
  /** Let the reader turn the body. Off where a pair must stay comparable. */
  orbit?: boolean;
  /**
   * Front-view camera zoom. 1.3 leaves room for the arms in the narrow
   * front/side pairs; a frame that is roomier than tall can afford more.
   */
  zoom?: number;
  className?: string;
}> = ({
  model,
  side,
  projected,
  generated,
  showMeasurement = '',
  orbit = false,
  zoom = 1.3,
  className = '',
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [hint, setHint] = useState(false);
  // Stable while the model is: the 3D view reloads and re-measures its mesh
  // whenever this object changes, and a new one every render made each
  // re-render of the page — every tick of the target slider — a reload.
  const models = useMemo(() => ({ models: model ? [model] : [], activeModel: 0 }), [model]);

  // A plain scroll wheel belongs to the page. It is stopped here, on the way
  // down to the canvas, so the orbit controls never see it and the page
  // scrolls as if the canvas were not there; a short hint says how to zoom
  // instead. A pinch — touch, or a trackpad, which the browser reports as a
  // wheel with ctrlKey — and Ctrl/⌘ + wheel go through and zoom.
  useEffect(() => {
    const el = frameRef.current;
    if (!orbit || !el) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) return;
      e.stopPropagation();
      setHint(true);
      clearTimeout(timer);
      timer = setTimeout(() => setHint(false), 1400);
    };
    el.addEventListener('wheel', onWheel, { capture: true });
    return () => {
      el.removeEventListener('wheel', onWheel, { capture: true });
      clearTimeout(timer);
    };
  }, [orbit]);

  return (
    <div
      ref={frameRef}
      data-testid="twin-view"
      data-orbit={orbit || undefined}
      className={`relative overflow-hidden rounded-lg bg-secondary ${
        projected ? 'projected-frame' : 'border border-border'
      } ${className}`}
    >
      {hint && (
        <span
          data-testid="zoom-hint"
          className="pointer-events-none absolute inset-x-2 bottom-2 z-10 rounded bg-foreground/80 px-2 py-1 text-center text-[10.5px] font-medium text-background"
        >
          {ZOOM_HINT}
        </span>
      )}
      {model && (
        <Canvas camera={{ fov: 40, zoom: side ? 1.6 : zoom, near: 1, far: 1100 }}>
          <color attach="background" args={['#fbfaf8']} />
          <Suspense fallback={null}>
            <ThreeDModel
              models={models}
              activeModel={0}
              showMeasurement={showMeasurement}
              rotationY={side ? Math.PI / 2 : Math.PI}
              tiltCorrection={generated ? GENERATED_TILT_RADIANS : 0}
              // Off by default. These really are meshes rather than renders —
              // 6890 vertices apiece — but a front/side pair only reads as a
              // pair while both stay put, so the panels that exist to be
              // compared keep their angle and the ones that exist to be
              // examined turn.
              orbit={orbit}
            />
          </Suspense>
        </Canvas>
      )}
    </div>
  );
};
